import React, { useEffect, useMemo } from 'react';
import { useAuth0 } from "@auth0/auth0-react";
import { 
    Box,
    LinearProgress,
    Tooltip,
} from '@mui/material';
import {
    CheckCircleOutline,
    CancelOutlined,
    ErrorOutlineOutlined,
} from '@mui/icons-material';
import {
    DataGridPro,
    LicenseInfo
} from '@mui/x-data-grid-pro';


LicenseInfo.setLicenseKey('17ca2e8476fa658618680f9f135dbc81T1JERVI6MzU2MTYsRVhQSVJZPTE4NzMzODQyMDIwMDAsS0VZVkVSU0lPTj0x',);


const renderOnsetAssetStatus = (params) => {
    return params.value !== null ? (
        <Tooltip title={params.value}>
            <Box
                display="flex"
                justifyContent="center"
                alignItems="center"
                sx={{
                    backgroundColor: 'rgb(76, 175, 80)',
                    borderRadius: '50%',
                    width: 24,
                    height: 24,
                }}
            >
                <CheckCircleOutline sx={{ fontSize: 18 }} />
            </Box>
        </Tooltip>
    ) : (
        <Box display="flex" justifyContent="center" alignItems="center" sx={{ backgroundColor: 'rgb(239, 83, 80)', borderRadius: '50%', width: 24, height: 24 }}>
            <CancelOutlined />
        </Box>
    );
};

const renderCompareAssetStatus = (params) => {
    return params.value === null ? (
        <Box display="flex" justifyContent="center" alignItems="center" sx={{ backgroundColor: 'rgb(239, 83, 80)', borderRadius: '50%', width: 24, height: 24 }}>
            <CancelOutlined />
        </Box>
    ) : params.row?.OnSet === params.value ? (
        <Tooltip title={params.value}>
            <Box
                display="flex"
                justifyContent="center"
                alignItems="center"
                sx={{
                    backgroundColor: 'rgb(76, 175, 80)',
                    borderRadius: '50%',
                    width: 24,
                    height: 24,
                }}
            >
                <CheckCircleOutline sx={{ fontSize: 18 }} />
            </Box>
        </Tooltip>
    ) : (
        <Tooltip title={params.value}>
            <Box display="flex" justifyContent="center" alignItems="center" sx={{ backgroundColor: 'rgb(255, 213, 79)', borderRadius: '50%', width: 24, height: 24 }}>
                <ErrorOutlineOutlined />
            </Box>
        </Tooltip>
    )
};



export default function AssetLabrollDataGrid(props) {
    const {
        labroll,
        retrieveAssetLabRoll,
        rows,
        rowsLoading,
    } = props
    const { isAuthenticated } = useAuth0();

    const columns = [
            {field: 'LabRoll',              headerName: 'Labroll',              width: 110 },
            {field: 'ClipName',             headerName: 'Clip Name',            width: 180},
            {field: 'duration',               headerName: 'Duration',             width: 120},
            {field: 'OnSet',                 headerName: 'OnSet',                 width: 70, renderCell: renderOnsetAssetStatus },
            {field: 'Lab',                  headerName: 'Lab',                  width: 70, renderCell: renderCompareAssetStatus },
            {field: 'Archive',                  headerName: 'Archive',                  width: 70, renderCell: renderCompareAssetStatus },
        ];

    // Conditionally hide the below 'targetHeaders' if all values are empty in that column
    //   Useful for ensuring S3 hashes aren't shown on projects that don't use S3 hashes
    const columnVisibilityModel = useMemo(() => {
        if (!rows || rows.length === 0) return {};
        const hidden = {};
        for (const col of columns) {
            // Exempt columns with renderCell
            if (col.renderCell) continue;
            const targetHeaders = ['Archive MD5', 'Archive XXH64'];
            if (!targetHeaders.includes(col.headerName)) continue;
            const allEmpty = rows.every(r => {
                const v = r[col.field];
                return v === undefined || v === null || v === '';
            });
            if (allEmpty) hidden[col.field] = false; // hide only if all empty
        }
        return hidden;
    }, [rows, columns]);

    useEffect(() => {
        isAuthenticated && (() => {
            retrieveAssetLabRoll({ labroll: labroll })
        })();
    }, [isAuthenticated, labroll])

    return (
        <React.Fragment>
                <DataGridPro 
                    sx={{
                        backgroundColor: '#101010', // Change the background color of the data grid
                        borderColor: '#122218',
                        '& .MuiDataGrid-root': {
                            cursor: 'pointer', // Change the cursor to pointer when hovering over the data grid
                        },
                        '& .MuiDataGrid-cell:hover': {
                            color: 'rgb(76, 146, 107)',
                        },
                        '& .MuiDataGrid-columnSeparator': {
                            borderColor: 'green', // Make the separator green
                            borderWidth: '2px', // Set the thickness of the separator
                            visibility: 'visible', // Ensure the separator always visible
                            '&:hover': {
                                borderColor: 'rgb(76, 146, 107)', // Ensure the separator stays green on hover
                            },
                        },
                        '& .MuiDataGrid-columnHeaders': {
                            backgroundColor: '#101010', // Change the column header background to black
                        },
                        '& .MuiDataGrid-columnHeader': {
                            fontSize: '14px', // Customize the font size of the column headers
                            color: 'white', // Change the column header color to white
                            fontWeight: 'bold', // Make the column header text bold
                            borderBottom: 'None', // Change the bottom line color of the column headers
                        },
                        '& .MuiDataGrid-row': {
                            border: 'none', // Remove the row separator lines
                        },
                        '& .MuiDataGrid-rowCount': {
                            color: 'white', // Change the background color of the selected row
                        },
                        '& .MuiDataGrid-selectedRowCount': {
                            color: 'white', // Change the font color of the selected row count
                        },
                        '& .MuiDataGrid-cell': {
                            border: 'none', // Remove the cell separator lines
                            color: 'white', // Change the font color of the cells
                            fontSize: '13px', // Make the cell text smaller
                            userSelect: 'text'
                        },
                        '& .MuiDataGrid-row:nth-of-type(odd)': {
                            backgroundColor: '#161618', // Change the background color of odd rows
                        },
                        '& .MuiDataGrid-row:nth-of-type(even)': {
                            backgroundColor: '#202020', // Change the background color of even rows
                        },
                        '& .MuiDataGrid-row:hover': {
                            backgroundColor: 'rgba(76, 146, 107, 0.1)', // Change the hover color of the row to light green with reduced opacity
                        },
                        '& .MuiDataGrid-cell--withRenderer': {
                            '& .MuiCheckbox-root': {
                                color: 'white', // Change the color of the checkbox
                                transform: 'scale(0.7)', // Make the checkbox smaller
                                '& .MuiSvgIcon-root': {
                                    borderRadius: '50%', // Make the checkbox a circle
                                },
                                '&.Mui-checked': {
                                    color: '#54a277', // Change the color of the selected checkbox to green
                                },
                            },
                        },
                        '& .MuiDataGrid-columnHeaderCheckbox': {
                            '& .MuiCheckbox-root': {
                                color: 'white', // Change the color of the checkbox in the column header
                                transform: 'scale(0.8)', // Make the checkbox smaller in the column header
                                '& .MuiSvgIcon-root': {
                                    borderRadius: '50%', // Make the checkbox a circle in the column header
                                },
                                '&.Mui-checked': {
                                    color: '#54a277', // Change the color of the selected checkbox to green in the column header
                                },
                            },
                        },
                    }}
                    columns={columns}
                    loading={rowsLoading}
                    pageSize={4}
                    rows={rows}
                    columnVisibilityModel={columnVisibilityModel}

                />
        </React.Fragment>
    )
 }
